import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { ref, set } from 'firebase/database';
import { auth, database } from '../services/firebase';
import './Register.css';

export default function Register() {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { returnTo?: string } | null;
  const returnTo = locationState?.returnTo;
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [prefix, setPrefix] = useState('');
  const [suffix, setSuffix] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isOrganizationAccount, setIsOrganizationAccount] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const displayName = useMemo(() => {
    const parts = [prefix, firstName, lastName, suffix].map((part) => part.trim()).filter(Boolean);
    return parts.join(' ').trim();
  }, [firstName, lastName, prefix, suffix]);

  useEffect(() => {
    setError(null);
  }, [firstName, lastName, email, password, prefix, suffix]);

  const mapFirebaseError = useCallback((code: string, fallback?: string) => {
    switch (code) {
      case 'auth/email-already-in-use':
        return 'That email is already registered. Try signing in instead.';
      case 'auth/invalid-email':
        return 'Enter a valid email address.';
      case 'auth/weak-password':
        return 'Choose a stronger password (at least 6 characters).';
      case 'auth/operation-not-allowed':
        return 'Email/password accounts are disabled for this project. Enable Email/Password in Firebase Authentication settings.';
      default:
        return fallback ?? 'Unable to create account. Please try again.';
    }
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!firstName.trim() || !lastName.trim()) {
        setError('First and last name are required.');
        return;
      }

      if (!email.trim() || !password) {
        setError('Email and password are required.');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
        const user = credential.user;

        if (displayName) {
          await updateProfile(user, { displayName }).catch((err) => {
            console.warn('Failed to update display name', err);
          });
        }

      await set(ref(database, `users/${user.uid}`), {
        uid: user.uid,
        email: user.email,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        prefix: prefix.trim() || null,
        suffix: suffix.trim() || null,
        displayName: displayName || null,
        createdAt: Date.now(),
        needsOrganizationSetup: isOrganizationAccount,
      }).catch((err) => {
        console.warn('Failed to persist user profile', err);
      });

        if (isOrganizationAccount) {
          navigate('/organization/create', {
            replace: true,
            state: returnTo ? { returnTo } : undefined,
          });
        } else {
          navigate('/my-pets', { replace: true });
        }
      } catch (err) {
        const code =
          typeof err === 'object' && err && 'code' in err ? String((err as { code?: unknown }).code) : '';
        const message =
          typeof err === 'object' && err && 'message' in err ? String((err as { message?: unknown }).message) : undefined;
        console.warn('Failed to create Firebase user', err);
        setError(mapFirebaseError(code, message));
      } finally {
        setLoading(false);
      }
    },
    [
      firstName,
      lastName,
      prefix,
      suffix,
      email,
      password,
      displayName,
      navigate,
      mapFirebaseError,
      returnTo,
      isOrganizationAccount,
    ]
  );

  const choiceBlock = (
    <div className="register-choice">
      <p className="register-choice-label">Who are you creating this account for?</p>
      <div className="register-choice-buttons">
        <button
          type="button"
          className={`register-choice-button ${isOrganizationAccount ? 'is-selected' : ''}`}
          onClick={() => {
            setIsOrganizationAccount(true);
          }}
          disabled={loading}
        >
          <span className="register-choice-text">
            <span className="register-choice-title">Organization</span>
            <span className="register-choice-subtitle">
              I'm creating this account for an organization (veterinary office, boarding service, grooming, etc.)
            </span>
          </span>
        </button>
        <button
          type="button"
          className={`register-choice-button ${!isOrganizationAccount ? 'is-selected' : ''}`}
          onClick={() => {
            setIsOrganizationAccount(false);
          }}
          disabled={loading}
        >
          <span className="register-choice-text">
            <span className="register-choice-title">Personal</span>
            <span className="register-choice-subtitle">I'm creating this account for personal use</span>
          </span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="register-root">
      <div className="register-card">
        <h1 className="register-title">Create Account</h1>
        {showDetails ? (
          <form className="register-form" onSubmit={handleSubmit}>
            {isOrganizationAccount ? (
              <>
                <input
                  className="register-input"
                  id="prefix"
                  type="text"
                  autoComplete="honorific-prefix"
                  placeholder="Prefix (Dr.)"
                  value={prefix}
                  onChange={(event) => setPrefix(event.target.value)}
                  disabled={loading}
                />
              </>
            ) : null}

            <input
              className="register-input"
              id="firstName"
              type="text"
              autoComplete="given-name"
              placeholder="First name"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              disabled={loading}
            />

            <input
              className="register-input"
              id="lastName"
              type="text"
              autoComplete="family-name"
              placeholder="Last name"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              disabled={loading}
            />

            {isOrganizationAccount ? (
              <input
                className="register-input"
                id="suffix"
                type="text"
                autoComplete="honorific-suffix"
                placeholder="Suffix (DVM)"
                value={suffix}
                onChange={(event) => setSuffix(event.target.value)}
                disabled={loading}
              />
            ) : null}

            <input
              className="register-input"
              id="email"
              type="email"
              autoComplete="email"
              placeholder="Email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={loading}
            />

            <input
              className="register-input"
              id="password"
              type="password"
              autoComplete="new-password"
              placeholder="Password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={loading}
            />

            {error ? (
              <p className="register-error" role="alert">
                {error}
              </p>
            ) : null}

            <button className="register-button" type="submit" disabled={loading}>
              {loading ? 'Creating your account…' : 'Sign Up'}
            </button>
          </form>
        ) : (
          <div className="register-choice-only">
            {choiceBlock}
            <button
              type="button"
              className="register-button"
              onClick={() => setShowDetails(true)}
              disabled={loading}
            >
              Continue
            </button>
          </div>
        )}

        <button
          type="button"
          className="register-secondary-button"
          disabled={loading}
          onClick={() =>
            navigate('/signin', {
              state: returnTo ? { returnTo } : undefined,
            })}
        >
          Back to Sign In
        </button>
      </div>
    </div>
  );
}
